import { ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PayloadAction } from '@reduxjs/toolkit';

import { smsTemplateService } from '@/services/smsTemplateService';
import {
  fetchTemplatesRequested,
  fetchTemplatesSucceeded,
  fetchTemplatesFailed,
  fetchTemplateRequested,
  fetchTemplateSucceeded,
  fetchTemplateFailed,
  createTemplateRequested,
  createTemplateSucceeded,
  createTemplateFailed,
  updateTemplateRequested,
  updateTemplateSucceeded,
  updateTemplateFailed,
  deleteTemplateRequested,
  deleteTemplateSucceeded,
  deleteTemplateFailed,
} from '../slices/smsTemplateSlice';
import { SmsTemplateCreate, SmsTemplateUpdate } from '@/types/sms';

/**
 * Epic to fetch all SMS templates
 */
export const fetchTemplatesEpic = (action$: any) =>
  action$.pipe(
    ofType(fetchTemplatesRequested.type),
    switchMap((action: PayloadAction<{
      lob_type?: 'MOTOR' | 'HEALTH' | 'ALL';
      sms_type?: 'notice_only' | 'notice_with_link';
      is_active?: boolean;
    } | undefined>) => 
      from(smsTemplateService.getTemplates(action.payload)).pipe(
        map((templates) => fetchTemplatesSucceeded(templates)),
        catchError((error) => {
          const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch templates';
          return of(fetchTemplatesFailed(errorMessage));
        })
      )
    )
  );

/**
 * Epic to fetch a single SMS template
 */
export const fetchTemplateEpic = (action$: any) =>
  action$.pipe(
    ofType(fetchTemplateRequested.type),
    switchMap((action: PayloadAction<number>) => 
      from(smsTemplateService.getTemplate(action.payload)).pipe(
        map((template) => fetchTemplateSucceeded(template)),
        catchError((error) => {
          const errorMessage = error.response?.data?.detail || error.message || 'Failed to fetch template';
          return of(fetchTemplateFailed(errorMessage));
        })
      )
    )
  );

/**
 * Epic to create a new SMS template
 */
export const createTemplateEpic = (action$: any) =>
  action$.pipe(
    ofType(createTemplateRequested.type),
    switchMap((action: PayloadAction<SmsTemplateCreate>) => 
      from(smsTemplateService.createTemplate(action.payload)).pipe(
        map((template) => createTemplateSucceeded(template)),
        catchError((error) => {
          const errorMessage = error.response?.data?.detail || error.message || 'Failed to create template';
          return of(createTemplateFailed(errorMessage));
        })
      )
    )
  );

/**
 * Epic to update an SMS template
 */
export const updateTemplateEpic = (action$: any) =>
  action$.pipe(
    ofType(updateTemplateRequested.type),
    switchMap((action: PayloadAction<{ id: number; data: SmsTemplateUpdate }>) => 
      from(smsTemplateService.updateTemplate(action.payload.id, action.payload.data)).pipe(
        map((template) => updateTemplateSucceeded(template)),
        catchError((error) => {
          const errorMessage = error.response?.data?.detail || error.message || 'Failed to update template';
          return of(updateTemplateFailed(errorMessage));
        })
      )
    )
  );

/**
 * Epic to delete (deactivate) an SMS template
 */
export const deleteTemplateEpic = (action$: any) =>
  action$.pipe(
    ofType(deleteTemplateRequested.type),
    switchMap((action: PayloadAction<number>) => 
      from(smsTemplateService.deleteTemplate(action.payload)).pipe(
        map(() => deleteTemplateSucceeded(action.payload)),
        catchError((error) => {
          const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete template';
          return of(deleteTemplateFailed(errorMessage));
        })
      )
    )
  );




